// TimelineView.jsx

import React, { useCallback, useMemo } from 'react';

// Context
import { useCalendarTimeline } from 'context/CalendarTimelineContext';
import { useCurrentLanguage } from 'context/LanguageContext';
import { useTheme } from 'context/ThemeContext';

// Utilities
import { getEnumOptionLabel } from 'utilities/EnumOptions';
import { getPluralLabel } from 'utilities/Languages';

// Hooks
import useDefaultNodeOpen from 'views/hooks/useDefaultNodeOpen';
import useTimelineEntries from 'views/timeline/hooks/useTimelineEntries';

// Utils
import { sortTimelineEntriesByStart } from 'views/timeline/utils/sortTimelineEntriesByStart';

// CSS
import './TimelineView.css';

// Components
import TimelineDayView from './views/day/TimelineDayView';
import TimelineMonthView from './views/month/TimelineMonthView';
import TimelineQuarterView from './views/quarter/TimelineQuarterView';
import TimelineWeekView from './views/week/TimelineWeekView';
import TimelineYearView from './views/year/TimelineYearView';

const TIMELINE_VIEW_COMPONENTS = {
    DAY: TimelineDayView,
    WEEK: TimelineWeekView,
    MONTH: TimelineMonthView,
    QUARTER: TimelineQuarterView,
    YEAR: TimelineYearView,
};

/**
 * TimelineView
 *
 * Controller view for Timeline. Keeps fixed left rows and swaps right-side timeline subviews.
 *
 * @returns {ReactNode} - TimelineView.
 */
const TimelineView = React.memo(({ onItemClick = null, view, typeDef, nodes = [] }) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();
    const language = useCurrentLanguage();
    const { timelineViewType } = useCalendarTimeline();

    // ----- HANDLERS ----- //

    const handleOpenNode = useDefaultNodeOpen();

    // ----- HOOKS ----- //

    const {
        timelineField,
        timelineFieldDef,
        groupFieldDef,
        effectiveTimelineRowMode,
        showUnscheduled,
        showDependencies,
        allowScheduleEdit,
        scheduledEntries,
        unscheduledEntries,
    } = useTimelineEntries({
        view,
        typeDef,
        nodes,
    });

    // ----- MEMOS ----- //

    const timelineItemsHeaderLabel = useMemo(() => (
        getPluralLabel(typeDef, typeDef?.key, language) || 'Items'
    ), [typeDef, language]);

    const unscheduledGroupLabel = useMemo(() => {
        const pluralLabel = timelineItemsHeaderLabel;
        return `Unscheduled ${pluralLabel}:`;
    }, [timelineItemsHeaderLabel]);

    // Hierarchy depth/order now resolved in useTimelineEntries via edge parentKey.
    const withHierarchyDepth = scheduledEntries;

    const timelineRows = useMemo(() => {
        if (effectiveTimelineRowMode === 'grouped') {
            const groups = new Map();

            withHierarchyDepth.forEach((entry) => {
                const rawTokens = Array.isArray(entry?.groupTokens) && entry.groupTokens.length
                    ? entry.groupTokens
                    : [{ key: '__UNGROUPED__', kind: 'ungrouped', label: 'Ungrouped' }];

                rawTokens.forEach((token, tokenIndex) => {
                    const tokenKey = String(token?.key || '').trim() || `__UNGROUPED___${tokenIndex}`;
                    if (!groups.has(tokenKey)) {
                        groups.set(tokenKey, {
                            token,
                            entries: [],
                        });
                    }
                    groups.get(tokenKey).entries.push(entry);
                });
            });

            const rows = [];
            groups.forEach((group, key) => {
                const token = group?.token || null;
                const entries = Array.isArray(group?.entries) ? group.entries : [];

                const groupLabel = (() => {
                    if (token?.kind === 'enum') {
                        const enumValue = String(token?.enumValue || '').trim();
                        if (!enumValue) return 'Ungrouped';
                        return getEnumOptionLabel(groupFieldDef?.options || [], enumValue, language) || enumValue;
                    }
                    if (token?.kind === 'people' || token?.kind === 'value') {
                        const tokenLabel = String(token?.label || '').trim();
                        return tokenLabel || 'Ungrouped';
                    }
                    return 'Ungrouped';
                })();

                rows.push({
                    type: 'group-header',
                    key: `group_${key}`,
                    label: groupLabel,
                    count: entries.length,
                });

                sortTimelineEntriesByStart(entries).forEach((entry, entryIndex) => {
                    rows.push({
                        type: 'item',
                        key: `item_${key}_${entry.key || entry.title || 'untitled'}_${entryIndex}`,
                        entry,
                    });
                });
            });

            if (showUnscheduled && unscheduledEntries.length > 0) {
                rows.push({
                    type: 'group-header',
                    key: 'group_unscheduled',
                    label: unscheduledGroupLabel,
                    count: unscheduledEntries.length,
                });
                unscheduledEntries.forEach((entry) => {
                    rows.push({
                        type: 'item-unscheduled',
                        key: `unscheduled_${entry.key || entry.title}`,
                        entry,
                    });
                });
            }

            return rows;
        }

        const rows = withHierarchyDepth.map((entry) => ({
            type: 'item',
            key: `item_${entry.key || entry.title}`,
            entry,
        }));

        if (showUnscheduled && unscheduledEntries.length > 0) {
            rows.push({
                type: 'group-header',
                key: 'group_unscheduled',
                label: unscheduledGroupLabel,
                count: unscheduledEntries.length,
            });

            unscheduledEntries.forEach((entry) => {
                rows.push({
                    type: 'item-unscheduled',
                    key: `unscheduled_${entry.key || entry.title}`,
                    entry,
                });
            });
        }

        return rows;
    }, [
        effectiveTimelineRowMode,
        withHierarchyDepth,
        showUnscheduled,
        unscheduledGroupLabel,
        unscheduledEntries,
        groupFieldDef?.options,
        language,
    ]);

    // ----- HANDLERS ----- //

    /**
 * Handles node click.
 *
 * @param {*} event
 * @param {*} node
 */

    const handleNodeClick = useCallback((event, node) => {
        event?.preventDefault?.();
        if (typeof onItemClick === 'function') {
            event?.stopPropagation?.();
            onItemClick(node);
            return;
        }
        handleOpenNode(node);
    }, [handleOpenNode, onItemClick]);

    /**
 * Handles node double click.
 *
 * @param {*} event
 * @param {*} node
 */
    const handleNodeDoubleClick = useCallback((event, node) => {
        event?.preventDefault?.();
        if (typeof onItemClick === 'function') {
            event?.stopPropagation?.();
            return;
        }
        handleOpenNode(node);
    }, [handleOpenNode, onItemClick]);
    const activeType = String(timelineViewType || 'WEEK').toUpperCase();
    const ActiveTimelineView = TIMELINE_VIEW_COMPONENTS[activeType] || TimelineWeekView;

    // ----- MEMOS ----- //

    const viewportBounds = useMemo(() => {
        const scheduleWindow = view?.scheduleWindow;
        if (!scheduleWindow || typeof scheduleWindow !== 'object') return null;

        const windowStartMs = Number(scheduleWindow.windowStartMs);
        const windowEndMs = Number(scheduleWindow.windowEndMs);
        if (!Number.isFinite(windowStartMs) || !Number.isFinite(windowEndMs)) return null;
        if (windowEndMs < windowStartMs) return null;

        return {
            start: new Date(windowStartMs),
            end: new Date(windowEndMs),
        };
    }, [view?.scheduleWindow]);

    // ----- RENDER ----- //

    return (
        <div
          className="timeline-view-container"
        >

            {/* SHELL */}
            <div
              className="timeline-view-shell"
            >
                {timelineField && (
                    <div
                      className="timeline-layout"
                      style={{
                        border: `1px solid ${theme.status}`,
                      }}
                    >

                        {/* TIMELINE LAYOUT LEFT */}
                        <div
                          className="timeline-layout-left"
                          style={{
                            borderRight: `1px solid ${theme.status}`,
                          }}
                        >

                            {/* TIMELINE LAYOUT LEFT HEADER */}
                            <div
                              className="timeline-layout-left-header"
                              style={{
                                borderBottom: `1px solid ${theme.status}`,
                                minHeight: activeType === 'YEAR' ? '30px' : '60px',
                              }}
                            >
                                {timelineItemsHeaderLabel}
                            </div>

                            {/* TIMELINE LAYOUT LEFT BODY */}
                            <div className="timeline-layout-left-body">
                                {timelineRows.map((row, rowIndex) => {
                                    const rowDividerStyle = rowIndex > 0
                                        ? { borderTop: `1px solid ${theme.status}` }
                                        : undefined;

                                    if (row.type === 'group-header') {
                                        return (
                                            <div
                                              className="timeline-layout-left-row timeline-layout-left-row-group"
                                              style={{
                                                ...rowDividerStyle,
                                              }}
                                              key={row.key}
                                            >

                                                {/* TIMELINE LAYOUT LEFT COUNT */}
                                                {row.label} <div className="timeline-layout-left-count">{row.count}</div>
                                            </div>
                                        );
                                    }

                                    const entry = row.entry;
                                    return (
                                        <div
                                          className="timeline-layout-left-row timeline-layout-left-row-item"
                                          style={{
                                            ...rowDividerStyle,
                                            paddingLeft: `${20 + ((entry?.depth || 0) * 14)}px`,
                                          }}
                                          key={row.key}
                                          title={entry?.title || 'Untitled'}
                                          onClick={(event) => handleNodeClick(event, entry?.node)}
                                          onDoubleClick={(event) => handleNodeDoubleClick(event, entry?.node)}
                                        >
                                            {entry?.title || 'Untitled'}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* TIMELINE LAYOUT RIGHT */}
                        <div className="timeline-layout-right">
                            <ActiveTimelineView
                              timelineRows={timelineRows}
                              timelineFieldDef={timelineFieldDef}
                              typeDef={typeDef}
                              viewportBounds={viewportBounds}
                              onNodeClick={handleNodeClick}
                              onNodeDoubleClick={handleNodeDoubleClick}
                              showDependencies={showDependencies}
                              allowScheduleEdit={allowScheduleEdit}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});

// Dev Tools
TimelineView.displayName = 'TimelineView';

export default TimelineView;
